// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

import { memo, type FC } from 'react';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { PlayerActionStatusEnum, type PlayerInfo } from '../types/game';
import { formatPlayerName } from '../utils/helpers';

interface PlayerCardProps {
  player: PlayerInfo;
  currentPlayerId: string;
  actionStatus: string;
}

// Memoized player card component to prevent re-renders of unchanged cards
const PlayerCard: FC<PlayerCardProps> = memo(({ player, currentPlayerId, actionStatus }) => {
  const isExpectedToAct = actionStatus === PlayerActionStatusEnum.EXPECTED_TO_ACT;
  const hasActed = actionStatus === PlayerActionStatusEnum.ACTION_TAKEN;

  // Background color based on status
  let bgClass = 'bg-white/10';
  let statusText = 'Idle';
  if (player.is_judge) {
    bgClass = 'bg-purple-500/30 border-2 border-purple-400';
    statusText = 'Psychologist';
  } else if (isExpectedToAct) {
    bgClass = 'bg-yellow-500/20 border-2 border-yellow-400';
    statusText = 'Thinking...';
  } else if (hasActed) {
    bgClass = 'bg-green-500/20 border-2 border-green-400';
    statusText = 'Done';
  }

  return (
    <div className={`p-2 sm:p-3 rounded-lg ${bgClass}`}>
      <div className="flex flex-col sm:flex-row items-center sm:items-center gap-2">
        <div className="relative flex-shrink-0">
          <div
            className={`h-10 w-10 sm:h-8 sm:w-8 rounded-lg flex items-center justify-center font-bold text-white ${player.profile_emoji ? 'text-xl sm:text-base' : 'text-xs sm:text-sm'}`}
            style={{
              backgroundColor: player.theme_primary_color || '#a855f7',
            }}
          >
            {player.profile_emoji || player.display_name.charAt(0).toUpperCase()}
          </div>
          {/* Connection indicator */}
          <div
            className={`absolute -bottom-1 -right-1 h-3 w-3 rounded-full border-2 border-gray-900 ${
              player.is_connected ? 'bg-green-500' : 'bg-red-500'
            }`}
            title={player.is_connected ? 'Connected' : 'Disconnected'}
          />
        </div>
        <div className="flex-1 min-w-0 text-center sm:text-left">
          <p className="text-xs sm:text-xs font-medium truncate">
            {formatPlayerName(player.display_name, player.player_id === currentPlayerId)}
          </p>
          <div className="flex items-center justify-center sm:justify-between gap-1 sm:gap-0">
            <span className="text-xs text-gray-400 hidden sm:inline">Score:</span>
            <span className="font-bold text-sm">{player.score}</span>
          </div>
          <p className="text-xs sm:text-[10px] text-gray-400 mt-0.5">{statusText}</p>
        </div>
      </div>
    </div>
  );
});

PlayerCard.displayName = 'PlayerCard';

interface ScoreboardProps {
  players: PlayerInfo[];
  currentPlayerId: string;
  playerActionStatuses: Record<string, string>;
}

// Memoize the entire Scoreboard to prevent re-renders when props haven't changed
export const Scoreboard = memo(function Scoreboard({
  players,
  currentPlayerId,
  playerActionStatuses,
}: ScoreboardProps) {
  const [parent] = useAutoAnimate();
  return (
    <div className="glass rounded-lg p-3 sm:p-4 mb-6" data-testid="scoreboard">
      <div
        ref={parent}
        className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:gap-3"
      >
        {players.map(player => {
          const playerActionStatus = playerActionStatuses[player.player_id] || 'idle';

          return (
            <PlayerCard
              key={player.player_id}
              player={player}
              currentPlayerId={currentPlayerId}
              actionStatus={playerActionStatus}
            />
          );
        })}
      </div>
    </div>
  );
});
